import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Spinner, Select } from '../../../shared/components';
import { ContactChannel, ContactChannelType, ContactMetadata } from '../types/Contact';
import { createContact, getContactMetadata } from '../services/contactService';
import { contactChannelTypeOptions, createEmptyContactChannel, ensurePrimaryContactChannels } from '../contactChannelUtils';
import { languageOptions, normalizeLanguageValue } from '../contactLanguageUtils';
import styles from './ContactFormLayout.module.css';

export const AddContact: React.FC = () => {
  const { t } = useTranslation(['contacts', 'common']);
  const navigate = useNavigate();
  const [loadingMetadata, setLoadingMetadata] = React.useState(true);
  const [metadata, setMetadata] = React.useState<ContactMetadata | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<{ [k: string]: string }>({});
  const [form, setForm] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    status: '',
    language: '',
    reminderPreference: '',
    marketingEmails: false,
    channels: [createEmptyContactChannel('phone')] as ContactChannel[]
  });

  React.useEffect(() => {
    const loadMetadata = async () => {
      setLoadingMetadata(true);
      try {
        const data = await getContactMetadata();
        setMetadata(data);
        setForm(prev => ({
          ...prev,
          status: prev.status || data.statuses[0]?.value || '',
          language: normalizeLanguageValue(prev.language),
          reminderPreference: prev.reminderPreference || data.reminderPreferences[0]?.value || '',
        }));
      } catch {
        setGeneralError(t('failedToLoadContactMetadata', { ns: 'contacts', defaultValue: 'Failed to load contact form options.' }));
      } finally {
        setLoadingMetadata(false);
      }
    };

    void loadMetadata();
  }, [t]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('title', { ns: 'contacts' }), path: '/contacts' },
    { label: t('addTitle', { ns: 'contacts' }), path: '/contacts/add' }
  ];

  const validate = () => {
    const nextErrors: { [k: string]: string } = {};

    if (!form.firstName.trim()) {
      nextErrors.firstName = t('firstNameRequired', { ns: 'contacts' });
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = t('invalidEmailAddress', { ns: 'contacts' });
    }

    const hasContactMethod = Boolean(form.email.trim() || form.channels.some(channel => channel.value.trim().length > 0));
    if (!hasContactMethod) {
      nextErrors.channels = t('channelsRequired', { ns: 'contacts' });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const setChannel = (index: number, updates: Partial<ContactChannel>) => {
    setForm(prev => {
      const currentChannel = prev.channels[index];
      if (!currentChannel) {
        return prev;
      }

      const nextChannels = prev.channels.map((channel, channelIndex) => {
        if (channelIndex !== index) return channel;
        return { ...channel, ...updates };
      });

      if (updates.isPrimary) {
        const type = nextChannels[index]?.type;
        return {
          ...prev,
          channels: nextChannels.map((channel, channelIndex) => channel.type === type ? { ...channel, isPrimary: channelIndex === index } : channel),
        };
      }

      return { ...prev, channels: nextChannels };
    });
  };

  const addChannel = () => {
    setForm(prev => ({ ...prev, channels: [...prev.channels, createEmptyContactChannel('phone')] }));
  };

  const removeChannel = (index: number) => {
    setForm(prev => {
      const remaining = prev.channels.filter((_, channelIndex) => channelIndex !== index);
      return { ...prev, channels: remaining.length > 0 ? ensurePrimaryContactChannels(remaining) : [createEmptyContactChannel('phone')] };
    });
  };

  const onSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setGeneralError(null);
    try {
      const normalizedChannels = ensurePrimaryContactChannels(form.channels)
        .filter(channel => channel.value.trim().length > 0)
        .map(channel => ({
          id: channel.id,
          type: channel.type,
          label: channel.label.trim() || undefined,
          value: channel.value.trim(),
          isPrimary: channel.isPrimary,
        }));

      const phoneChannel = normalizedChannels.find(channel => channel.type === 'phone');
      const whatsAppChannel = normalizedChannels.find(channel => channel.type === 'whatsApp');

      await createContact({
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        email: form.email.trim() || undefined,
        title: form.title.trim() || undefined,
        phoneNumber: phoneChannel?.value || undefined,
        whatsApp: whatsAppChannel?.value || undefined,
        status: form.status || undefined,
        language: form.language || undefined,
        reminderPreference: form.reminderPreference || undefined,
        marketingEmails: form.marketingEmails,
        channels: normalizedChannels,
      });
      navigate('/contacts');
    } catch {
      setGeneralError(t('failedToAddContact', { ns: 'contacts' }));
    } finally {
      setSaving(false);
    }
  };

  if (loadingMetadata) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loadingContactMetadata', { ns: 'contacts', defaultValue: 'Loading contact form options…' })} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={breadcrumbItems} />
      <div className={styles.header}>
        <h2 className={styles.title}>{t('addTitle', { ns: 'contacts' })}</h2>
      </div>
      {generalError && <div className={`error ${styles.errorBanner}`}>{generalError}</div>}

      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label className={styles.label}>
            {t('firstName', { ns: 'contacts' })}
            <input className={styles.input} value={form.firstName} onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))} />
          </label>
          {errors.firstName && <div className={styles.fieldError}>{errors.firstName}</div>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('lastName', { ns: 'contacts' })}
            <input className={styles.input} value={form.lastName} onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))} />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('email', { ns: 'contacts' })}
            <input className={styles.input} value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} />
          </label>
          {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('titleField', { ns: 'contacts' })}
            <input className={styles.input} value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('status', { ns: 'contacts' })}
            <Select
              value={form.status}
              onChange={(value) => setForm(prev => ({ ...prev, status: value }))}
              options={(metadata?.statuses || []).map(option => ({
                value: option.value,
                label: option.label,
              }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('language', { ns: 'contacts' })}
            <Select
              value={form.language}
              onChange={(value) => setForm(prev => ({ ...prev, language: value }))}
              options={languageOptions.map(option => ({
                value: option.code,
                label: option.label,
              }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('reminderPreference', { ns: 'contacts' })}
            <Select
              value={form.reminderPreference}
              onChange={(value) => setForm(prev => ({ ...prev, reminderPreference: value }))}
              options={(metadata?.reminderPreferences || []).map(option => ({
                value: option.value,
                label: option.label,
              }))}
            />
          </label>
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.primaryToggle}>
            <input type="checkbox" checked={form.marketingEmails} onChange={e => setForm(prev => ({ ...prev, marketingEmails: e.target.checked }))} />
            <span>{t('marketingEmails', { ns: 'contacts' })}</span>
          </label>
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <div className={styles.channelsHeader}>
            <label className={styles.label}>{t('channels', { ns: 'contacts' })}</label>
            <button type="button" className="btn btn-secondary" onClick={addChannel}>{t('addChannel', { ns: 'contacts' })}</button>
          </div>
          <div className={styles.fieldHint}>{t('telegramChannelManagedHint', { ns: 'contacts' })}</div>
          <div className={styles.channelsList}>
            {form.channels.map((channel, index) => (
              <div key={`${channel.type}-${index}`} className={styles.channelRow}>
                <div className={styles.channelGrid}>
                  <label className={styles.label}>
                    {t('channelType', { ns: 'contacts' })}
                    <Select
                      value={channel.type}
                      onChange={(value) => setChannel(index, { type: value as ContactChannelType, isPrimary: channel.isPrimary })}
                      options={contactChannelTypeOptions.map(type => ({
                        value: type,
                        label: t(`channelTypeOptions.${type}`, { ns: 'contacts' }),
                      }))}
                    />
                  </label>
                  <label className={styles.label}>
                    {t('channelLabel', { ns: 'contacts' })}
                    <input className={styles.input} value={channel.label} onChange={e => setChannel(index, { label: e.target.value })} placeholder={t('channelLabelPlaceholder', { ns: 'contacts' })} />
                  </label>
                  <label className={styles.label}>
                    {t('channelValue', { ns: 'contacts' })}
                    <input className={styles.input} value={channel.value} onChange={e => setChannel(index, { value: e.target.value })} placeholder={t('channelValuePlaceholder', { ns: 'contacts' })} />
                  </label>
                  <label className={styles.primaryToggle}>
                    <input type="radio" name={`primary-${channel.type}`} checked={channel.isPrimary} onChange={() => setChannel(index, { isPrimary: true })} />
                    <span>{t('primary', { ns: 'contacts' })}</span>
                  </label>
                </div>
                <div className={styles.channelActions}>
                  <button type="button" className="btn btn-secondary" onClick={() => removeChannel(index)} disabled={form.channels.length === 1}>{t('removeChannel', { ns: 'contacts' })}</button>
                </div>
                {channel.type === 'telegram' ? <div className={styles.fieldHint}>{t('telegramChannelReadOnlyHint', { ns: 'contacts' })}</div> : null}
              </div>
            ))}
          </div>
          <div className={styles.fieldHint}>{t('channelsHint', { ns: 'contacts' })}</div>
          {errors.channels && <div className={styles.fieldError}>{errors.channels}</div>}
        </div>
      </div>

      <div className={styles.actions}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'contacts' })}</button>
        <button className="btn btn-secondary" onClick={() => navigate('/contacts')} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
      </div>
    </div>
  );
};

export default AddContact;